import React from 'react';

import {Sequence} from 'components/Sequence';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import {getVerticalPlacement} from 'molbio/maps/linear/basesOverlayUtil';
import {timesPerSlice, whichDisplay} from 'molbio/maps/linear/LayeringHelpers';
import PolymerSearchResultRegion from 'molbio/maps/linear/PolymerSearchResultRegion';

interface NucleotidesSearchResultsProps {
  sequence: Sequence;
  sliceStart: number;
  sliceEnd: number;
  showingComplement: boolean;
  basesHeight: number;
  xFn: (index: number) => number;
}

export default function NucleotidesSearchResults({
  sequence,
  sliceStart,
  sliceEnd,
  showingComplement,
  basesHeight,
  xFn,
}: NucleotidesSearchResultsProps): JSX.Element {
  const searchData = SearchDNAData.get();
  const searchResults = searchData
    .get('results')
    .slice(sliceStart, sliceEnd, sequence, searchData.get('onlyShowSelected'));

  // In plasmids, search results may cover both ends of the slice with a gap in the middle, so two rects
  // are needed to draw it. Collect objects representing each region a result needs to be rendered.
  const displaySearchRegions = searchResults.flatMap((searchResult) =>
    timesPerSlice(searchResult, sliceStart, sliceEnd, searchResult.start(), searchResult.end()).map(
      (_, i) => ({
        searchResult,
        ...whichDisplay(
          sliceStart,
          sliceEnd,
          searchResult.start(),
          searchResult.end(),
          i,
          sequence.length()
        )!,
      })
    )
  );

  return (
    <>
      {displaySearchRegions.map(({start, end, searchResult}) => (
        <PolymerSearchResultRegion
          key={`${start}-${searchResult.cid}`}
          xFn={xFn}
          regionStartIndex={start}
          regionLength={end - start}
          isSelected={searchResult.selected()}
          onClick={() => {
            searchResult.click(sequence);
          }}
          {...getVerticalPlacement(showingComplement, basesHeight, searchResult.strand())}
        />
      ))}
    </>
  );
}
